import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, imgSrc, money, vipPrice } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useVip } from "@/hooks/useVip";
import { Reveal, SectionLabel } from "./Reveal";

const STATUS = {
  selling_fast: ["Selling fast", "bg-[#C9CDD4] text-black"],
  sold_out: ["Sold out", "bg-zinc-700 text-zinc-200"],
  on_sale: ["On sale", "border border-white/30 text-zinc-200"],
};

const PriceLine = ({ label, price, member, testId }) => (
  <div data-testid={testId} className="text-sm whitespace-nowrap">
    <span className="text-zinc-400 group-hover:text-black/70 mr-2">{label}</span>
    {member ? (
      <>
        <span className="line-through text-zinc-500 group-hover:text-black/50 mr-2">{money(price)}</span>
        <span className="text-[#56ED4A] group-hover:text-black font-semibold text-base">{money(vipPrice(price))}</span>
      </>
    ) : (
      <span className="font-semibold">{money(price)}</span>
    )}
  </div>
);

const FlyerThumb = ({ show, i }) => {
  const [open, setOpen] = useState(false);
  const src = imgSrc(show.image_url);
  return (
    <>
      <button
        type="button"
        data-testid={`show-flyer-${i}`}
        onClick={() => setOpen(true)}
        aria-label={`View ${show.title} flyer`}
        className="relative shrink-0 w-full max-w-[240px] sm:w-[120px] md:w-[140px] aspect-[4/5] overflow-hidden bg-black border border-white/15 -rotate-2 group-hover:rotate-0 group-hover:scale-105 transition-transform duration-500 shadow-[8px_8px_0_0_rgba(0,0,0,0.6)]"
      >
        <img src={src} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-xl opacity-40" />
        <img src={src} alt={`${show.title} flyer`} loading="lazy" className="absolute inset-0 w-full h-full object-contain" />
        <span className="absolute bottom-0 inset-x-0 bg-black/75 font-mono text-[10px] uppercase tracking-[0.2em] text-[#56ED4A] py-1 text-center">View flyer</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`show-flyer-dialog-${i}`} className="bg-black border-white/10 w-[94vw] max-w-2xl p-3">
          <DialogTitle className="sr-only">{show.title} flyer</DialogTitle>
          <img src={src} alt={`${show.title} flyer`} className="w-full max-h-[80vh] object-contain" />
          {show.ticket_url && show.status !== "sold_out" && (
            <a href={show.ticket_url} target="_blank" rel="noopener noreferrer" data-testid={`show-flyer-tickets-${i}`} className="mt-2 inline-flex items-center justify-center gap-2 bg-[#56ED4A] text-black py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold">
              Get tickets <ArrowUpRight size={16} />
            </a>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const ShowRow = ({ show, vip, i }) => {
  const d = new Date(`${show.date}T12:00:00`);
  const [label, cls] = STATUS[show.status] || STATUS.on_sale;
  const sold = show.status === "sold_out";
  return (
    <motion.article
      data-testid={`show-row-${i}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className="group relative grid grid-cols-[80px_1fr] md:grid-cols-[140px_1fr_auto] gap-5 md:gap-10 items-center border-t border-white/10 py-7 md:py-9 px-2 md:px-4 overflow-hidden"
    >
      <span className="absolute inset-0 bg-[#56ED4A] origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] -z-0" />
      <div className="relative z-10 group-hover:text-black transition-colors duration-300">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#56ED4A] group-hover:text-black">{d.toLocaleDateString("en-US", { month: "short" })} · {d.toLocaleDateString("en-US", { weekday: "short" })}</div>
        <div className="font-display text-6xl md:text-8xl leading-none">{String(d.getDate()).padStart(2, "0")}</div>
      </div>
      <div className="relative z-10 min-w-0 flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center">
        {show.image_url && <FlyerThumb show={show} i={i} />}
      <div className="min-w-0 group-hover:text-black transition-colors duration-300">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <span className={`font-mono text-[11px] uppercase tracking-[0.2em] px-2 py-1 ${cls}`}>{label}</span>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400 group-hover:text-black/70">{show.region} · Doors {show.time}</span>
        </div>
        <h3 className="font-display uppercase text-3xl md:text-5xl leading-[0.95] break-words">{show.title}</h3>
        <p className="mt-2 text-zinc-300 group-hover:text-black/80">{show.venue} — {show.city}</p>
        {show.lineup && <p className="font-mono text-xs uppercase tracking-[0.15em] mt-2 text-zinc-400 group-hover:text-black/70">w/ {show.lineup}</p>}
      </div>
      </div>
      <div className="relative z-10 col-span-2 md:col-span-1 flex md:flex-col items-center md:items-end justify-between gap-4 group-hover:text-black">
        <div className="text-left md:text-right font-mono flex flex-col gap-1">
          <PriceLine label="GA" price={show.price} member={!!vip} testId={`show-ga-price-${i}`} />
          {show.vip_price != null && <PriceLine label="VIP" price={show.vip_price} member={!!vip} testId={`show-vip-price-${i}`} />}
          {vip && <span className="text-[11px] uppercase tracking-[0.15em] text-[#56ED4A] group-hover:text-black">Exclusive −$5</span>}
        </div>
        {show.ticket_url && !sold ? (
          <a data-testid={`show-tickets-btn-${i}`} href={show.ticket_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#F4F4F6] text-black group-hover:bg-black group-hover:text-[#56ED4A] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold transition-colors">
            Tickets <ArrowUpRight size={16} />
          </a>
        ) : (
          <span data-testid={`show-tickets-soon-${i}`} className="font-mono text-xs uppercase tracking-[0.2em] border border-current px-5 py-3 opacity-80">{sold ? "Sold out" : "Tickets soon"}</span>
        )}
      </div>
    </motion.article>
  );
};

export const Shows = () => {
  const [shows, setShows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("All");
  const vip = useVip();
  useEffect(() => {
    api.get("/shows").then((r) => setShows(Array.isArray(r.data) ? r.data : [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const regions = useMemo(() => ["All", ...new Set(shows.map((s) => s.region).filter(Boolean))], [shows]);
  const list = filter === "All" ? shows : shows.filter((s) => s.region === filter);

  return (
    <section id="shows" data-testid="shows-section" className="px-5 md:px-10 py-24 md:py-36">
      <Reveal>
        <SectionLabel index="01">Upcoming shows</SectionLabel>
        <div className="mt-6 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.85]">On the <span className="text-[#56ED4A]">calendar</span></h2>
          <div className="flex flex-wrap gap-2">
            {regions.map((r) => (
              <button key={r} data-testid={`shows-filter-${r.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(r)} className={`font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${filter === r ? "bg-[#56ED4A] text-black border-[#56ED4A]" : "border-white/20 text-zinc-300 hover:border-white"}`}>{r}</button>
            ))}
          </div>
        </div>
        {vip && <p data-testid="shows-vip-banner" className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-[#56ED4A]">JayPanduh Exclusive active · show code {vip} at the door / checkout for $5 off every show</p>}
      </Reveal>
      <div className="mt-14 border-b border-white/10">
        {list.map((s, i) => <ShowRow key={s.id} show={s} vip={vip} i={i} />)}
        {loaded && !list.length && <p data-testid="shows-empty" className="py-16 text-zinc-400 font-mono uppercase tracking-[0.2em] text-sm">New dates dropping soon. Get on the list below.</p>}
      </div>
    </section>
  );
};
